import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Checkbox,
  Collapse,
  Tag,
  Typography,
  Divider,
  Alert,
  Descriptions,
  Popconfirm,
  message,
  Spin,
} from 'antd';
import { SaveOutlined, ReloadOutlined, CopyOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import { integrationService } from '@services/admin/integrationService';
import { webhookService } from '@services/admin/webhookService';

const { Text, Paragraph } = Typography;

// Scope categories (matches backend Integration::AVAILABLE_SCOPES)
const SCOPE_GROUPS = {
  orders: ['orders:read', 'orders:write'],
  products: ['products:read', 'products:write'],
  inbounds: ['inbounds:read', 'inbounds:write'],
  inventory: ['inventory:read'],
  shipments: ['shipments:read'],
  return_orders: ['return_orders:read'],
  webhooks: ['webhooks:manage'],
};
const ALL_SCOPES = Object.values(SCOPE_GROUPS).flat();

// Event categories (matches backend Webhook::AVAILABLE_EVENTS)
const EVENT_GROUPS = {
  order: [
    'order.created', 'order.updated', 'order.cancelled',
    'order.shipped', 'order.completed', 'order.on_hold',
  ],
  inbound: ['inbound.created', 'inbound.completed', 'inbound.cancelled'],
  inventory: ['inventory.updated', 'inventory.low_stock'],
  shipment: ['shipment.created', 'shipment.shipped'],
  return_order: ['return_order.created', 'return_order.completed'],
  product: ['product.created', 'product.updated'],
};
const ALL_EVENTS = Object.values(EVENT_GROUPS).flat();

export default function AdminIntegrationEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [regeneratingWebhookSecret, setRegeneratingWebhookSecret] = useState(false);
  const [ipWhitelistText, setIpWhitelistText] = useState('');
  const [ipWhitelistError, setIpWhitelistError] = useState(null);

  useEffect(() => {
    if (!isNew) fetchIntegration();
  }, [id]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const data = await integrationService.get(id);
      if (data.success && data.data) {
        const d = data.data;
        setIntegration(d);
        form.setFieldsValue({
          name: d.name,
          description: d.description,
          webhook_url: d.webhook?.url || '',
        });
        setSelectedScopes(d.scopes || []);
        setSelectedEvents(d.webhook?.events || []);
        setIpWhitelistText((d.ip_whitelist || []).join('\n'));
      }
    } catch (error) {
      message.error(t('shared.integrations.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (selectedScopes.length === 0) {
      message.warning(t('shared.integrations.form.scopes_hint'));
      return;
    }

    if (ipWhitelistText.trim() && !validateIpWhitelist(ipWhitelistText)) {
      return;
    }

    try {
      setSaving(true);
      const ipList = parseIpList(ipWhitelistText);

      const payload = {
        name: values.name,
        description: values.description,
        scopes: selectedScopes,
        ip_whitelist: ipList,
      };

      // Attach webhook if URL is provided
      if (values.webhook_url) {
        payload.webhook = {
          url: values.webhook_url,
          events: selectedEvents,
        };
      }

      const data = isNew
        ? await integrationService.create(payload)
        : await integrationService.update(id, payload);

      if (data.success) {
        if (isNew && data.data.access_token_created) {
          // 新建 integration 必須顯示一次性的 client_id / access_token；
          // 此情況下不能直接導向 show 頁，否則使用者會失去 token。
          // 由 createdCredentials 畫面內的「View Integration」按鈕負責後續跳轉。
          setCreatedCredentials({
            client_id: data.data.client_id,
            access_token: data.data.access_token,
            integration_id: data.data.id,
          });
        } else {
          message.success(t('shared.integrations.messages.update_success'));
          // 更新成功 → 導向該筆記錄的 show 頁
          // 非 token-creation 的 isNew 路徑（理論上不會發生）回退到列表頁
          navigate(isNew ? '/integrations' : `/integrations/${id}`, { replace: true });
        }
      } else {
        message.error(data.message || t('shared.integrations.error.create_failed'));
      }
    } catch (error) {
      message.error(isNew ? t('shared.integrations.error.create_failed') : t('shared.integrations.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  // ===== Scopes helpers =====
  const handleScopeSelectAll = () => setSelectedScopes(ALL_SCOPES);
  const handleScopeDeselectAll = () => setSelectedScopes([]);

  const handleScopeCategoryToggle = (scopes) => {
    const allSelected = scopes.every((s) => selectedScopes.includes(s));
    setSelectedScopes(allSelected
      ? selectedScopes.filter((s) => !scopes.includes(s))
      : [...new Set([...selectedScopes, ...scopes])]);
  };

  const handleScopeToggle = (scope) => {
    setSelectedScopes(selectedScopes.includes(scope)
      ? selectedScopes.filter((s) => s !== scope)
      : [...selectedScopes, scope]);
  };

  // ===== Events helpers =====
  const handleEventSelectAll = () => setSelectedEvents(ALL_EVENTS);
  const handleEventDeselectAll = () => setSelectedEvents([]);

  const handleEventCategoryToggle = (events) => {
    const allSelected = events.every((e) => selectedEvents.includes(e));
    setSelectedEvents(allSelected
      ? selectedEvents.filter((e) => !events.includes(e))
      : [...new Set([...selectedEvents, ...events])]);
  };

  const handleEventToggle = (event) => {
    setSelectedEvents(selectedEvents.includes(event)
      ? selectedEvents.filter((e) => e !== event)
      : [...selectedEvents, event]);
  };

  // ===== IP Whitelist validation =====
  const IP_V4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
  const IP_V6 = /^[0-9a-fA-F:]+(::\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})?(\/\d{1,3})?$/;

  const parseIpList = (text) =>
    text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const validateIpWhitelist = (text) => {
    const lines = parseIpList(text);
    const invalid = lines.filter((ip) => !IP_V4.test(ip) && !IP_V6.test(ip));
    if (invalid.length > 0) {
      setIpWhitelistError(
        t('shared.integrations.form.ip_whitelist_invalid', { ips: invalid.join(', ') })
      );
      return false;
    }
    setIpWhitelistError(null);
    return true;
  };

  const handleCopyCredentials = () => {
    if (createdCredentials) {
      navigator.clipboard.writeText(`Client ID: ${createdCredentials.client_id}\nAccess Token: ${createdCredentials.access_token}`);
      message.success(t('shared.integrations.messages.copy_success'));
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('shared.integrations.messages.copy_success'));
  };

  const handleRegenerateToken = async () => {
    try {
      setRegeneratingToken(true);
      const data = await integrationService.regenerateToken(id);
      if (data.success) {
        setIntegration(data.data);
        message.success(t('shared.integrations.messages.regenerate_success'));
      }
    } catch (error) {
      message.error(t('shared.integrations.error.update_failed'));
    } finally {
      setRegeneratingToken(false);
    }
  };

  const handleRegenerateWebhookSecret = async () => {
    if (!integration?.webhook?.id) return;
    try {
      setRegeneratingWebhookSecret(true);
      const data = await webhookService.regenerateSecret(integration.webhook.id);
      if (data.success) {
        message.success(t('shared.webhooks.messages.regenerate_success'));
        fetchIntegration();
      }
    } catch (error) {
      message.error(t('shared.webhooks.error.update_failed'));
    } finally {
      setRegeneratingWebhookSecret(false);
    }
  };

  // ===== Created credentials screen =====
  if (createdCredentials) {
    return (
      <div>
        <ListPageHeader basePath="/admin" items={['developer_tools', 'developer_tools.integrations']} />
        <Card title={t('shared.integrations.messages.create_success')}>
          <Alert
            message={t('shared.integrations.detail.token_security_notice')}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('shared.integrations.fields.client_id')}</Text>
            <div><Text code copyable style={{ fontSize: 14 }}>{createdCredentials.client_id}</Text></div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <Text type="secondary">{t('shared.integrations.fields.access_token')}</Text>
            <div><Text code copyable style={{ fontSize: 14 }}>{createdCredentials.access_token}</Text></div>
          </div>
          <Space>
            <Button onClick={handleCopyCredentials}>
              {t('shared.integrations.actions.copy_credentials')}
            </Button>
            <Button type="primary" onClick={() => navigate(`/integrations/${createdCredentials.integration_id}`)}>
              {t('shared.integrations.actions.view_integration')}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // ===== Collapse items: Scopes =====
  const scopeCollapseItems = Object.entries(SCOPE_GROUPS).map(([category, scopes]) => {
    const count = scopes.filter((s) => selectedScopes.includes(s)).length;
    const all = count === scopes.length;
    return {
      key: category,
      label: (
        <Space>
          <Checkbox checked={all} indeterminate={count > 0 && !all}
            onChange={() => handleScopeCategoryToggle(scopes)} onClick={(e) => e.stopPropagation()} />
          <Text strong>{t(`shared.integrations.scope_categories.${category}`)}</Text>
          <Tag>{count}/{scopes.length}</Tag>
        </Space>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {scopes.map((scope) => (
            <Checkbox key={scope} checked={selectedScopes.includes(scope)} onChange={() => handleScopeToggle(scope)}>
              <Space><Text code>{scope}</Text><Text type="secondary">{t(`shared.integrations.scopes.${scope.replace(':', '_')}`, scope)}</Text></Space>
            </Checkbox>
          ))}
        </Space>
      ),
    };
  });

  // ===== Collapse items: Events =====
  const eventCollapseItems = Object.entries(EVENT_GROUPS).map(([category, events]) => {
    const count = events.filter((e) => selectedEvents.includes(e)).length;
    const all = count === events.length;
    return {
      key: category,
      label: (
        <Space>
          <Checkbox checked={all} indeterminate={count > 0 && !all}
            onChange={() => handleEventCategoryToggle(events)} onClick={(e) => e.stopPropagation()} />
          <Text strong>{t(`shared.webhooks.event_categories.${category}`)}</Text>
          <Tag>{count}/{events.length}</Tag>
        </Space>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {events.map((event) => (
            <Checkbox key={event} checked={selectedEvents.includes(event)} onChange={() => handleEventToggle(event)}>
              <Space><Text code>{event}</Text><Text type="secondary">{t(`shared.webhooks.events.${event}`, event)}</Text></Space>
            </Checkbox>
          ))}
        </Space>
      ),
    };
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader basePath="/admin" items={['developer_tools', 'developer_tools.integrations']} />
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ maxWidth: 800 }}>
          {/* ====== 基本資訊 ====== */}
          <Form.Item
            name="name"
            label={t('shared.integrations.fields.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input placeholder={t('shared.integrations.placeholder.name')} />
          </Form.Item>

          <Form.Item name="description" label={t('shared.integrations.fields.description')}>
            <Input.TextArea rows={2} placeholder={t('shared.integrations.placeholder.description')} />
          </Form.Item>

          {/* ====== API Credentials & Security (edit mode only) ====== */}
          {!isNew && integration && (
            <>
              <Divider>{t('shared.integrations.detail.credentials')}</Divider>
              <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
                <Descriptions.Item label={t('shared.integrations.fields.client_id')}>
                  <Space>
                    <Text code>{integration.client_id}</Text>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(integration.client_id)}>
                      {t('shared.integrations.actions.copy')}
                    </Button>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('shared.integrations.fields.access_token')}>
                  <Space>
                    <Text code>
                      {showToken && integration.access_token ? integration.access_token : '••••••••••••••••••••••••••••••••'}
                    </Text>
                    <Button
                      size="small"
                      icon={showToken ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => setShowToken(!showToken)}
                    >
                      {showToken ? t('shared.integrations.actions.hide_token') : t('shared.integrations.actions.show_token')}
                    </Button>
                    {integration.access_token && (
                      <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(integration.access_token)}>
                        {t('shared.integrations.actions.copy')}
                      </Button>
                    )}
                    <Popconfirm
                      title={t('shared.integrations.confirm.regenerate_token')}
                      onConfirm={handleRegenerateToken}
                      okText={t('common.button.confirm')}
                      cancelText={t('common.button.cancel')}
                    >
                      <Button size="small" icon={<ReloadOutlined />} loading={regeneratingToken}>
                        {t('shared.integrations.actions.regenerate_token')}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              {/* ====== IP Whitelist (inside credentials/security) ====== */}
              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('shared.integrations.fields.ip_whitelist')}
                </Text>
                <Alert
                  message={t('shared.integrations.form.ip_whitelist_hint')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                <Input.TextArea
                  value={ipWhitelistText}
                  onChange={(e) => {
                    setIpWhitelistText(e.target.value);
                    if (ipWhitelistError) setIpWhitelistError(null);
                  }}
                  onBlur={() => {
                    if (ipWhitelistText.trim()) validateIpWhitelist(ipWhitelistText);
                  }}
                  placeholder={t('shared.integrations.placeholder.ip_whitelist')}
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  status={ipWhitelistError ? 'error' : undefined}
                />
                {ipWhitelistError && (
                  <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
                    {ipWhitelistError}
                  </Text>
                )}
              </div>
            </>
          )}

          {/* ====== IP Whitelist (new mode — no credentials yet) ====== */}
          {isNew && (
            <>
              <Divider>{t('shared.integrations.fields.ip_whitelist')}</Divider>
              <div style={{ marginBottom: 24 }}>
                <Alert
                  message={t('shared.integrations.form.ip_whitelist_hint')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                <Input.TextArea
                  value={ipWhitelistText}
                  onChange={(e) => {
                    setIpWhitelistText(e.target.value);
                    if (ipWhitelistError) setIpWhitelistError(null);
                  }}
                  onBlur={() => {
                    if (ipWhitelistText.trim()) validateIpWhitelist(ipWhitelistText);
                  }}
                  placeholder={t('shared.integrations.placeholder.ip_whitelist')}
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  status={ipWhitelistError ? 'error' : undefined}
                />
                {ipWhitelistError && (
                  <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
                    {ipWhitelistError}
                  </Text>
                )}
              </div>
            </>
          )}

          <Divider>{t('shared.integrations.fields.scopes')}</Divider>

          {/* ====== Scopes ====== */}
          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                {t('shared.integrations.form.scopes_hint')}
              </Paragraph>
              <Space>
                <Button size="small" onClick={handleScopeSelectAll}>{t('shared.integrations.form.select_all')}</Button>
                <Button size="small" onClick={handleScopeDeselectAll}>{t('shared.integrations.form.deselect_all')}</Button>
              </Space>
            </Space>
            {selectedScopes.length === 0 && (
              <Alert message={t('shared.integrations.form.scopes_hint')} type="warning" showIcon style={{ marginBottom: 12 }} />
            )}
            <Collapse items={scopeCollapseItems} defaultActiveKey={Object.keys(SCOPE_GROUPS)} bordered={false} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{selectedScopes.length} / {ALL_SCOPES.length}</Text>
            </div>
          </div>

          <Divider>{t('shared.integrations.detail.webhook')}</Divider>

          {/* ====== Webhook ====== */}
          <Alert
            message={t('shared.integrations.form.webhook_hint')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="webhook_url"
            label={t('shared.webhooks.fields.url')}
            rules={[{ type: 'url', message: t('shared.webhooks.form.url_hint') }]}
            extra={t('shared.webhooks.form.url_hint')}
          >
            <Input placeholder={t('shared.webhooks.placeholder.url')} />
          </Form.Item>

          {/* Webhook Secret Key (edit mode, webhook exists) */}
          {!isNew && integration?.webhook && (
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('shared.webhooks.detail.signature_info')}>
                <Space>
                  <Text code>{integration.webhook.secret_key || '********'}</Text>
                  <Popconfirm
                    title={t('shared.webhooks.confirm.regenerate_secret')}
                    onConfirm={handleRegenerateWebhookSecret}
                    okText={t('common.button.confirm')}
                    cancelText={t('common.button.cancel')}
                  >
                    <Button size="small" icon={<ReloadOutlined />} loading={regeneratingWebhookSecret}>
                      {t('shared.webhooks.actions.regenerate_secret')}
                    </Button>
                  </Popconfirm>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          )}

          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
              <Text strong>{t('shared.webhooks.fields.events')}</Text>
              <Space>
                <Button size="small" onClick={handleEventSelectAll}>{t('shared.integrations.form.select_all')}</Button>
                <Button size="small" onClick={handleEventDeselectAll}>{t('shared.integrations.form.deselect_all')}</Button>
              </Space>
            </Space>
            <Collapse items={eventCollapseItems} defaultActiveKey={Object.keys(EVENT_GROUPS)} bordered={false} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{selectedEvents.length} / {ALL_EVENTS.length}</Text>
            </div>
          </div>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                {isNew ? t('common.button.create') : t('common.button.save')}
              </Button>
              <Button onClick={() => navigate(isNew ? '/integrations' : `/integrations/${id}`)}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
